import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, Typography
} from "@mui/material";
import { sendInterviewEmail, sendRefusalEmail } from "../../../redux/Actions/teacherManagerActions";
import MessageCard from "../../MessageCard/MessageCard";
import { CONTACT_EMAIL } from "config/contact";

const NotifyTeacherModal = ({ open, onClose, teacher }) => {
  const dispatch = useDispatch();

  const [zoomLink, setZoomLink] = useState("");
  const [message, setMessage] = useState("");
  const [mode, setMode] = useState(null); // "INTERVIEW" | "REFUSAL"
  const [sending, setSending] = useState(false);

  // MessageCard state
  const [toastMsg, setToastMsg] = useState("");
  const [toastType, setToastType] = useState(""); // "success" | "error" | "info"

  const resetForm = () => {
    setZoomLink("");
    setMessage("");
    setMode(null);
    setSending(false);
    setToastMsg("");
    setToastType("");
  };

  // Clear everything when modal closes (or reopens)
  useEffect(() => {
    if (!open) resetForm();
  }, [open]);

  const generateAcceptanceMessage = () => {
    setMode("INTERVIEW");
    setMessage(
`Sehr geehrte/r ${teacher.firstName} ${teacher.lastName},

wir freuen uns, Ihnen mitteilen zu können, dass Ihre Bewerbung die erste Überprüfung bestanden hat.
Der nächste Schritt ist ein Interview über Zoom.

Zoom-Link: ${zoomLink}

Bitte erscheinen Sie pünktlich zum vereinbarten Termin.

Mit freundlichen Grüßen,
ALGermanist Education Team`
    );
  };

  const generateRefusalMessage = () => {
    setMode("REFUSAL");
    setMessage(
`Sehr geehrte/r ${teacher.firstName} ${teacher.lastName},

vielen Dank für Ihre Bewerbung bei ALGermanist Education.
Nach sorgfältiger Prüfung können wir Ihre Bewerbung aktuell nicht weiter berücksichtigen.

Wir wünschen Ihnen für Ihren weiteren Weg alles Gute.

Mit freundlichen Grüßen,
ALGermanist Education Team`
    );
  };

  const handleClose = () => {
    resetForm();
    onClose?.();
  };

  const onSend = async () => {
    try {
      if (mode === "INTERVIEW") {
        if (!zoomLink || zoomLink.trim().length < 8) {
          setToastMsg("Bitte geben Sie einen gültigen Zoom-Link ein.");
          setToastType("error");
          return;
        }
      }
      if (!message?.trim()) {
        setToastMsg("Bitte verfassen Sie eine Nachricht.");
        setToastType("error");
        return;
      }

      setSending(true);

      if (mode === "INTERVIEW") {
        // make sure action sends { zoomLink, message } to backend
        await dispatch(sendInterviewEmail(teacher.teacherId, zoomLink.trim(), message));
      } else if (mode === "REFUSAL") {
        await dispatch(sendRefusalEmail(teacher.teacherId, message));
      } else {
        setToastMsg("Bitte wählen Sie zuerst eine Nachricht (Interview oder Absage).");
        setToastType("error");
        return;
      }

      setToastMsg("✅ E-Mail wurde erfolgreich gesendet.");
      setToastType("success");

      // auto-close shortly after success (optional)
      setTimeout(() => {
        handleClose();
      }, 1200);
    } catch (err) {
      const apiMsg = err?.response?.data || "Senden fehlgeschlagen.";
      setToastMsg(`❌ ${apiMsg}`);
      setToastType("error");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Teacher Application Notification</DialogTitle>
      <DialogContent>

        {/* MessageCard at the top */}
        {!!toastMsg && (
          <Box sx={{ mb: 2 }}>
            <MessageCard
              message={toastMsg}
              type={toastType || "info"}
              onClose={() => setToastMsg("")}
            />
          </Box>
        )}

        <TextField label="From" fullWidth margin="normal" value={CONTACT_EMAIL} disabled />
        <TextField label="To" fullWidth margin="normal" value={teacher.email} disabled />

        <TextField
          label="Zoom Link (nur für Interview)"
          fullWidth
          margin="normal"
          value={zoomLink}
          onChange={(e) => setZoomLink(e.target.value)}
        />

        <Box sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            color="success"
            onClick={generateAcceptanceMessage}
            sx={{ mr: 1 }}
            disabled={sending}
          >
            Interview-Vorschau
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={generateRefusalMessage}
            disabled={sending}
          >
            Absage-Vorschau
          </Button>
        </Box>

        <TextField
          label="Nachricht"
          fullWidth
          multiline
          minRows={8}
          margin="normal"
          value={message}
          onChange={(e)=>setMessage(e.target.value)}
        />

        <Typography variant="caption" color="text.secondary">
          Hinweis: Für Interview-E-Mails ist ein Zoom-Link erforderlich.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onSend} variant="contained" disabled={sending}>
          {sending ? "Senden..." : "Send Message"}
        </Button>
        <Button onClick={handleClose} disabled={sending}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default NotifyTeacherModal;

